"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FungibleAsset = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const lodash_1 = require("lodash");
const Base_1 = require("../Base");
const internal_1 = require("../../../../internal");
const assets_1 = require("../../../../middleware/queries/assets");
const externalAgents_1 = require("../../../../middleware/queries/externalAgents");
const conversion_1 = require("../../../../utils/conversion");
const internal_2 = require("../../../../utils/internal");
const Settlements_1 = require("../Base/Settlements");
const AssetHolders_1 = require("./AssetHolders");
const Checkpoints_1 = require("./Checkpoints");
const CorporateActions_1 = require("./CorporateActions");
const Issuance_1 = require("./Issuance");
const Offerings_1 = require("./Offerings");
const TransferRestrictions_1 = require("./TransferRestrictions");
/**
 * Class used to manage all Fungible Asset functionality
 */
class FungibleAsset extends Base_1.BaseAsset {
    /**
     * @hidden
     */
    constructor(identifiers, context) {
        super(identifiers, context);
        this.settlements = new Settlements_1.FungibleSettlements(this, context);
        this.assetHolders = new AssetHolders_1.AssetHolders(this, context);
        this.issuance = new Issuance_1.Issuance(this, context);
        this.transferRestrictions = new TransferRestrictions_1.TransferRestrictions(this, context);
        this.offerings = new Offerings_1.Offerings(this, context);
        this.checkpoints = new Checkpoints_1.Checkpoints(this, context);
        this.corporateActions = new CorporateActions_1.CorporateActions(this, context);
        this.redeem = (0, internal_2.createProcedureMethod)({ getProcedureAndArgs: args => [internal_1.redeemTokens, Object.assign({ asset: this }, args)] }, context);
        this.controllerTransfer = (0, internal_2.createProcedureMethod)({ getProcedureAndArgs: args => [internal_1.controllerTransfer, Object.assign({ asset: this }, args)] }, context);
    }
    /**
     * Retrieve the identifier data (block number, date and event index) of the event that was emitted when the token was created
     *
     * @note uses the middlewareV2
     * @note there is a possibility that the data is not ready by the time it is requested. In that case, `null` is returned
     */
    createdAt() {
        return __awaiter(this, void 0, void 0, function* () {
            const { id, context } = this;
            const middlewareAssetId = yield (0, internal_2.getAssetIdForMiddleware)(id, context);
            const { data: { assets: { nodes: [asset], }, }, } = yield context.queryMiddleware((0, assets_1.assetQuery)({
                id: middlewareAssetId,
            }));
            return (0, internal_2.optionize)(conversion_1.middlewareEventDetailsToEventIdentifier)(asset === null || asset === void 0 ? void 0 : asset.createdBlock, asset === null || asset === void 0 ? void 0 : asset.eventIdx);
        });
    }
    /**
     * Retrieve the amount of unique investors that hold this Asset
     */
    investorCount() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query: { asset: { balanceOf }, }, }, }, context, } = this;
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(this, context);
            const balanceEntries = yield balanceOf.entries(rawAssetId);
            const assetBalances = balanceEntries.filter(([, balance]) => !(0, conversion_1.balanceToBigNumber)(balance).isZero());
            return new bignumber_js_1.default(assetBalances.length);
        });
    }
    /**
     * Retrieve this Asset's Operation History
     *
     * @note Operations are grouped by the agent Identity who performed them
     *
     * @note uses the middlewareV2
     */
    getOperationHistory() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context, id: assetId } = this;
            const middlewareAssetId = yield (0, internal_2.getAssetIdForMiddleware)(assetId, context);
            const { data: { tickerExternalAgentHistories: { nodes }, }, } = yield context.queryMiddleware((0, externalAgents_1.tickerExternalAgentHistoryQuery)(context.isSqIdPadded, {
                assetId: middlewareAssetId,
            }));
            const groupedData = (0, lodash_1.groupBy)(nodes, 'identityId');
            return (0, lodash_1.map)(groupedData, (history, did) => ({
                identity: new internal_1.Identity({ did }, context),
                history: history.map(({ createdBlock, eventIdx }) => 
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                (0, conversion_1.middlewareEventDetailsToEventIdentifier)(createdBlock, eventIdx)),
            }));
        });
    }
    /**
     * Retrieve this Asset's transaction History
     *
     * @note uses the middlewareV2
     */
    getTransactionHistory(opts) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context, id } = this;
            const { size, start } = opts;
            const middlewareAssetId = yield (0, internal_2.getAssetIdForMiddleware)(id, context);
            const { data: { assetTransactions: { nodes, totalCount }, }, } = yield context.queryMiddleware((0, assets_1.assetTransactionQuery)(context.isSqIdPadded, {
                assetId: middlewareAssetId,
            }, size, start));
            const data = [];
            for (const { asset, amount, fromPortfolioId, toPortfolioId, createdBlock, eventId, eventIdx, extrinsicIdx, fundingRound, instructionId, instructionMemo, } of nodes) {
                const fromPortfolio = (0, internal_2.optionize)(conversion_1.portfolioIdStringToPortfolio)(fromPortfolioId);
                const toPortfolio = (0, internal_2.optionize)(conversion_1.portfolioIdStringToPortfolio)(toPortfolioId);
                const assetId = (0, internal_2.getAssetIdFromMiddleware)(asset);
                data.push(Object.assign({ asset: new FungibleAsset({ assetId }, context), amount: new bignumber_js_1.default(amount).shiftedBy(-6), event: eventId, from: (0, internal_2.optionize)(conversion_1.middlewarePortfolioToPortfolio)(fromPortfolio, context), to: (0, internal_2.optionize)(conversion_1.middlewarePortfolioToPortfolio)(toPortfolio, context), fundingRound, instructionId: instructionId ? new bignumber_js_1.default(instructionId) : undefined, instructionMemo, 
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    extrinsicIndex: new bignumber_js_1.default(extrinsicIdx) }, (0, conversion_1.middlewareEventDetailsToEventIdentifier)(createdBlock, eventIdx)));
            }
            const count = new bignumber_js_1.default(totalCount);
            const next = (0, internal_2.calculateNextKey)(count, data.length, start);
            return {
                data,
                next,
                count,
            };
        });
    }
    /**
     * Determine whether this FungibleAsset exists on chain
     */
    exists() {
        return __awaiter(this, void 0, void 0, function* () {
            const { context, context: { polymeshApi: { query: { asset, nft }, }, }, } = this;
            const rawAssetId = (0, conversion_1.assetToMeshAssetId)(this, context);
            const [tokenSize, nftId] = yield Promise.all([
                asset.assets.size(rawAssetId),
                nft.collectionAsset(rawAssetId),
            ]);
            return !tokenSize.isZero() && nftId.isZero();
        });
    }
}
exports.FungibleAsset = FungibleAsset;
//# sourceMappingURL=index.js.map